import { Box, Button, Flex, Text } from "@chakra-ui/react";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAccount } from "wagmi";
// import { updateUserData } from "store";

import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  IS_STAKING_PAUSED,
  isNumber,
  MINT_LIMIT_CHAINS,
  RESTAKE_NOW,
  toFixedWithoutRounding,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";

import AddXSymbol from "@components/AddXSymbol";
import BaseImage from "@components/BaseImage";
import LstTokenSelect from "@components/LstTokenSelect";
import TicketInput from "@components/TicketInput";
import TicketInputRightSection from "@components/TicketInputRightSection";
import StakeConfirmation from "@components/StakeConfirmation";

import {
  APPROVE_STAKING_CLICK,
  CONNECT_WALLET_CLICK,
  MINT_LIMIT_CROSSED,
  STAKE_CTA,
} from "@constants/analytics";
import { ERDataType } from "@hooks/useExchangeRate";
import useLedgerLive from "@hooks/useLedgerLive";
import usePoolData from "@hooks/usePoolData";
import {
  emitReduxEvent,
  formatNumber,
  isFloat,
  numberToCurrency,
  toStringPrecision,
  trackEvent,
} from "../../utils";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import useDappChain from "@hooks/useDappChain";
import { ETH, WSTETH } from "@config/contractsConfig";
import useTokenBalance from "@hooks/useTokenBalance";
import useDappConfig from "@hooks/useDappConfig";
import Tooltip from "@components/Tooltip";

import { updateUserData } from "store";
import useDepositFee from "@hooks/useDepositFee";
import useDepositEstimate from "@hooks/useDepositEstimate";
import useUserType from "@hooks/useUserType";
import useAllowance from "@hooks/useAllowance";
import { useEstimatedGasFee } from "@hooks/useGasFeeEstimation";
import useMintLimit from "@hooks/useMintLimit";

const MAX_FRACTION_DIGITS = 4;

interface StakeComponentProps {
  conversionData: ERDataType;
  isActive: boolean;
}

const StakeComponent = ({ conversionData, isActive }: StakeComponentProps) => {
  const [stakeAmount, setStakeAmount] = useState("");
  const [error, setError] = useState("");
  const [isStakeConfirmModal, setIsStakeConfirmMoal] = useState(false);
  const { isStaderLedgerUser } = useUserType();

  const {
    remainingLimit,
    remainingLimitRsEth,
    isLoading: mintLimitLoading,
  } = useMintLimit();

  const { selectedERC20, referralId } = useSelector((state: any) => state.user);
  const conversions = useMemo(() => {
    const conversionToERC20 = conversionData?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionData?.exchangeRate]);

  const { isEthereumChain } = useDappChain();
  const { chainId } = useAccount();

  const poolData = usePoolData({
    enabled:
      selectedERC20 !== WSTETH &&
      ((isEthereumChain && selectedERC20 !== ETH) || !isEthereumChain),
    conversionRate: conversions.toERC20,
  });

  const isStakingFull = useMemo(() => {
    return [""].includes(selectedERC20);
  }, [selectedERC20]);

  const { erc20Allowance } = useAllowance();

  const { isConnected, address } = useAccount();

  const { nativeTokenBalance, tokensBalances, xTokenTokenBalance } =
    useTokenBalance();

  const { isLedgerConnected } = useLedgerLive();

  const tokenAmount =
    selectedERC20 === ETH
      ? nativeTokenBalance
      : tokensBalances[selectedERC20] || 0;

  const { openConnectModal } = useConnectModal();

  const { config, isRestakeSupported } = useDappConfig();

  const { estimatedGasFee } = useEstimatedGasFee();

  const { formatted: depositFeeP } = useDepositFee();

  const { returns } = useDepositEstimate(stakeAmount);

  const isPoolSizeExceeded = useMemo(() => {
    if (selectedERC20 === ETH) return false;
    return poolData?.poolLimit && poolData?.usedValue
      ? poolData.usedValue >= poolData.poolLimit
      : false;
  }, [poolData?.poolLimit, poolData?.usedValue, selectedERC20]);

  const checkInput = useCallback(
    (value = stakeAmount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenAmount) {
        error = `You can't restake more than available balance in your wallet.`;
      } else if (
        poolData &&
        poolData.poolLimit &&
        (Number(value) > poolData.poolLimit ||
          (poolData.usedValue &&
            poolData.usedValue + Number(value) > poolData.poolLimit))
      ) {
        error = `You can't restake more than pool size.`;
      } else if (chainId && MINT_LIMIT_CHAINS.includes(chainId)) {
        if (mintLimitLoading) {
          error = "Fetching wrsETH mint limit";
        } else if (!mintLimitLoading && !remainingLimit) {
          error =
            "Error fetching wrsETH mint limit, please try after sometime.";
        } else if (remainingLimit && Number(value) > Number(remainingLimit)) {
          trackEvent(MINT_LIMIT_CROSSED, { amount_entered: value });
          error = `You can only mint ${remainingLimitRsEth} wrsETH until 23:59 today. More wrsETH can be minted after UTC 00:00 tomorrow.`;
        }
      }

      return error;
    },
    [
      isConnected,
      nativeTokenBalance,
      poolData,
      selectedERC20,
      stakeAmount,
      tokensBalances,
      remainingLimit,
      remainingLimitRsEth,
    ]
  );

  useEffect(() => {
    if (stakeAmount !== "") {
      const error = checkInput(stakeAmount);
      setError(error);
    }
  }, [tokenAmount, nativeTokenBalance, address, stakeAmount, checkInput]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const priorityToken = Object.keys(config.tokens).find(
      (key) => config.tokens[key].priority
    );

    if (!priorityToken) {
      const isNativeTokenRestakeSupported =
        config.nativeToken.isRestakeSupported;
      dispatch(
        updateUserData({
          selectedERC20: isNativeTokenRestakeSupported
            ? config.nativeToken.symbol
            : Object.values(config.tokens)[0]?.symbol,
        })
      );
      return;
    }

    dispatch(
      updateUserData({
        selectedERC20: priorityToken!,
      })
    );
  }, [config.tokens, dispatch, isActive]);

  const isApproved = useMemo(() => {
    if (selectedERC20 === ETH) return true;

    const amount = stakeAmount || "";
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, selectedERC20, stakeAmount]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (isNumber(val) || val === "") {
      if (val.indexOf(".") === -1) {
        setStakeAmount(val);
      } else {
        if (val.length - (val.indexOf(".") + 1) <= MAX_FRACTION_DIGITS) {
          setStakeAmount(val === "." ? "0." : val);
        }
      }
    }
  };

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isRestakeSupported) {
        return "Switch network";
      } else if (isApproved) {
        return RESTAKE_NOW;
      }
      return APPROVE_CTA;
    }
  };

  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "center_wiget",
        cta: CONNECT_WALLET_CTA,
      });
    } else {
      if (!isRestakeSupported) {
        emitReduxEvent(EVENTS.SWITCH_NETWORK);
      } else if (isApproved) {
        if (!stakeAmount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }
        trackEvent(STAKE_CTA, {
          amount: stakeAmount,
        });
        if (isStakingFull) {
          setIsStakeConfirmMoal(true);
        } else {
          emitReduxEvent(EVENTS.HANDLE_STAKE, {
            amount: stakeAmount,
            referralId,
            isStaderLedgerUser,
          });
          setStakeAmount("");
        }
      } else {
        emitReduxEvent(EVENTS.HANDLE_ERC20_APPROVE, {
          amount: stakeAmount,
          referralId,
        });

        trackEvent(APPROVE_STAKING_CLICK, { asset: selectedERC20 });
      }
    }
  }, [
    isConnected,
    isRestakeSupported,
    isApproved,
    stakeAmount,
    isStakingFull,
    referralId,
    openConnectModal,
  ]);

  const handleMaxClick = useCallback(() => {
    if (selectedERC20 === ETH) {
      const amount = Math.max(
        0,
        tokenAmount - (Number(estimatedGasFee) || 0)
      ).toString();
      setStakeAmount(toFixedWithoutRounding(amount.toString(), 4));
    } else {
      setStakeAmount(toFixedWithoutRounding(tokenAmount.toString(), 4));
    }
  }, [tokenAmount]);

  const convertedAmount = useMemo(() => {
    if (stakeAmount && isNumber(stakeAmount)) {
      const value = Number(stakeAmount) * Number(conversions.fromERC20);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [stakeAmount, conversions]);

  return (
    <Box>
      <Box
        fontSize="14px"
        lineHeight="20px"
        fontWeight="400"
        color="textSecondary"
      >
        <LstTokenSelect />
        <Flex w="100%" alignItems="center">
          <Flex
            justifyContent="space-between"
            flexGrow={1}
            flexDir={{ base: "column", xl: "row" }}
          >
            <Box
              display={{ base: isConnected ? "none" : "inherit", xl: "block" }}
              h="20px"
            >
              <Text>Enter amount</Text>
            </Box>
            {isConnected && (
              <Flex
                alignItems="center"
                justifyContent={{ base: "space-between", xl: "center" }}
              >
                <Box
                  fontWeight={{ base: "700", md: "600" }}
                  fontSize={{ base: "12px", md: "14px" }}
                >
                  <Text as="span">My {config.xtoken.symbol} : </Text>
                  <Text as="span">
                    {formatNumber(
                      Number(xTokenTokenBalance),
                      false,
                      Number(xTokenTokenBalance) < 0.0001 ? 6 : 4,
                      true
                    ) || "-"}
                  </Text>
                </Box>

                {!isLedgerConnected && <AddXSymbol xSymbol={config.xtoken} />}
              </Flex>
            )}
          </Flex>
        </Flex>

        <TicketInput
          inputRightElement={
            <TicketInputRightSection
              isConnected={isConnected}
              tokenAmount={tokenAmount}
              onClick={handleMaxClick}
            />
          }
          value={stakeAmount}
          onChange={(value: string) => handleChange(value)}
          error={error}
        />

        {error && (
          <Flex alignItems="center" mt="8px">
            <BaseImage
              src="/assets/transactions/error.svg"
              width={20}
              height={20}
              alt="error-icon"
            />
            <Text ml="0.5rem" color="error" fontSize="12px" fontWeight="400">
              {error ? error : "error"}
            </Text>
          </Flex>
        )}

        <Box mt={{ base: "2rem", md: "1.5rem" }}>
          <Flex justifyContent="space-between">
            <Text>You will get</Text>
            <Text>
              {" "}
              {Number(returns)
                ? formatNumber(
                    Number(returns),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  )
                : formatNumber(
                    Number(convertedAmount),
                    false,
                    MAX_FRACTION_DIGITS,
                    true
                  ) || 0}{" "}
              {config.xtoken.symbol}
            </Text>
          </Flex>

          {conversions.toERC20 ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Text>Exchange rate</Text>
              <Text>
                1 {config.xtoken.symbol} ={" "}
                {toFixedWithoutRounding(conversions.toERC20.toString(), 4)}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}

          {/* {selectedERC20 === ETH ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Flex gap={2} alignItems="center">
                <Text>Rewards Fee</Text>
                <Tooltip
                  label={
                    <Text>
                      This fee applies to Staking rewards. No fee is charged on
                      EigenLayer Points.
                    </Text>
                  }
                />
              </Flex>
              <Text>10%</Text>
            </Flex>
          ) : null} */}

          {!isEthereumChain && poolData.poolLimit ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Text>Maximum deposit</Text>
              <Text>
                {numberToCurrency(poolData.poolLimit.toString())}{" "}
                {selectedERC20}
              </Text>
            </Flex>
          ) : null}
          {depositFeeP ? (
            <Flex justifyContent="space-between" mt="1rem">
              <Flex gap={2} alignItems="center">
                <Text>Minting fees</Text>
                <Tooltip
                  label={
                    <Text>Covers bridging, minting and restaking cost</Text>
                  }
                />
              </Flex>
              <Text>{depositFeeP * 100}%</Text>
            </Flex>
          ) : null}
        </Box>
      </Box>

      {/* <Flex
        mt="2.5rem"
        gap="16px"
        color="#403104"
        border="1px solid"
        borderColor="#DFCCB6"
        borderRadius="16px"
        bg="#F8F3E4"
        p="16px"
      >
        <Box>
          <AiOutlineInfoCircle fontSize="20px" />
        </Box>
        <Text
          fontSize="12px"
          fontWeight="400"
          lineHeight="20px"
          display="inline"
          color="#403104"
          flex="1 0 0"
        >
          LST deposits on EigenLayer are paused. Kelp DAO will convert your LSTs
          to native ETH and restake to earn you EigenLayer Points.
        </Text>
      </Flex> */}

      <Button
        borderRadius="12px"
        bg="textPrimary"
        p="16px 64px"
        h="56px"
        mt="12px"
        width="100%"
        _hover={{
          bg: "textPrimary",
        }}
        onClick={handleClick}
        isDisabled={
          !!error ||
          (isConnected && IS_STAKING_PAUSED) ||
          (isPoolSizeExceeded && isConnected) ||
          (!stakeAmount && isConnected)
        }
      >
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="600"
          color="backgroundCard"
        >
          {getButtonText()}
        </Text>
      </Button>

      {/* {isEthereumChain && selectedERC20 !== ETH && (
        <StakeLimit poolData={poolData} />
      )} */}

      {isStakingFull && (
        <StakeConfirmation
          isOpen={isStakeConfirmModal}
          onClose={() => setIsStakeConfirmMoal(false)}
          onPrimaryClick={() => {
            setIsStakeConfirmMoal(false);
            emitReduxEvent(EVENTS.HANDLE_STAKE, {
              amount: stakeAmount,
              referralId,
            });
            setStakeAmount("");
          }}
          // onSecondaryClick={() => {
          //   setIsStakeConfirmMoal(false);
          //   dispatch(
          //     updateUserData({
          //       selectedERC20: ETHX,
          //     })
          //   );
          // }}
        />
      )}
    </Box>
  );
};

export default StakeComponent;
